// Approve a queued payout request (contract §9 pins this file's path, default export and
// `{payout, open, onClose}` props exactly). Money-moving, so it is one of the six §4.1
// step-up-gated endpoints — `StepUpConfirmDialog` carries that flow; this file is just the
// payout-approval instance of it: the money summary, and the actual API call.
import React from "react";
import { toast } from "sonner";
import { useLanguage, useT } from "../../lib/LanguageContext";
import { formatMoneyLedger } from "../../lib/formatMoney";
import defaultGatewayApi from "../api";
import { extractGatewayErrorCode } from "../errors";
import StepUpConfirmDialog from "./StepUpConfirmDialog";

/**
 * @param {import("../contract").PayoutRequest} payout
 * @param {boolean} open
 * @param {function} onClose (didMutate: boolean) => void
 * @param {object} [gatewayApi] DI seam (contract §9)
 */
export default function PayoutApproveDialog({ payout, open, onClose, gatewayApi = defaultGatewayApi }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();

  const handleConfirm = async ({ stepUpToken }) => {
    try {
      await gatewayApi.adminApprovePayout(payout.payoutRequestId, stepUpToken);
      toast.success(t("gw.console.payouts.approved"));
      return { ok: true };
    } catch (e) {
      return { ok: false, code: extractGatewayErrorCode(e) };
    }
  };

  if (!payout) return null;

  return (
    <StepUpConfirmDialog
      open={open}
      onClose={onClose}
      titleKey="gw.console.payouts.approveTitle"
      titleVars={{ id: payout.payoutRequestId }}
      descKey="gw.console.payouts.approveDesc"
      requireAck
      ackKey="gw.console.payouts.approveAck"
      onConfirm={handleConfirm}
      gatewayApi={gatewayApi}
    >
      {/* WHOSE money, and how much of it actually moves. This summary named the
          requested amount and the request id and nothing else, so an operator completed
          a real re-authentication to release funds without being told which practice
          they belonged to, where they were going, or that the rolling reserve makes the
          transfer smaller than the figure on screen. */}
      <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm space-y-1.5" data-testid="approve-summary">
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">{t("gw.console.payouts.colTenant")}</span>
          <span className="font-semibold text-right">
            {payout.practice?.name || t("gw.console.payouts.unknownPractice")}
            <span className="block font-mono text-xs font-normal text-slate-500">{payout.tenantId}</span>
          </span>
        </div>
        {payout.practice?.ownerEmail ? (
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">{t("gw.console.payouts.colOwner")}</span>
            <span className="text-xs text-right">{payout.practice.ownerEmail}</span>
          </div>
        ) : null}
        <div className="flex justify-between">
          <span className="text-slate-500">{t("gw.console.payouts.colAmount")}</span>
          <span className="tabular-nums">{formatMoneyLedger(payout.amountCents, payout.currency, lang)}</span>
        </div>
        {typeof payout.reserveHeldCents === "number" && payout.reserveHeldCents > 0 ? (
          <div className="flex justify-between">
            <span className="text-slate-500">{t("gw.console.payouts.reserveHeld")}</span>
            <span className="tabular-nums">− {formatMoneyLedger(payout.reserveHeldCents, payout.currency, lang)}</span>
          </div>
        ) : null}
        {/* The bold figure is the one that leaves, because that is the number being
            approved. `null` means the row does not state it, and then the requested
            amount stays the only figure shown rather than a fabricated one. */}
        {typeof payout.transferAmountCents === "number" ? (
          <div className="flex justify-between border-t border-slate-200 pt-1.5">
            <span className="text-slate-500">{t("gw.console.payouts.leavesNow")}</span>
            <span className="font-bold tabular-nums" data-testid="approve-transfer-amount">{formatMoneyLedger(payout.transferAmountCents, payout.currency, lang)}</span>
          </div>
        ) : null}
        {payout.destinationAccountId ? (
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">{t("gw.console.payouts.destination")}</span>
            <span className="font-mono text-xs text-right" data-testid="approve-destination">{payout.destinationAccountId}</span>
          </div>
        ) : null}
        {payout.crossBorder ? (
          <div className="text-xs text-amber-700" data-testid="approve-cross-border">{t("gw.console.payouts.crossBorder")}</div>
        ) : null}
        <div className="flex justify-between">
          <span className="text-slate-500">{t("gw.console.payouts.colId")}</span>
          <span className="font-mono text-xs">{payout.payoutRequestId}</span>
        </div>
      </div>
    </StepUpConfirmDialog>
  );
}
